import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { type ApiError, errorMessage } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { AlertIcon } from '@/shared/ui/icons';
import { Logo } from '@/shared/ui/Logo';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Spinner } from '@/shared/ui/Spinner';

import { authApi } from './api';
import { MIN_PASSWORD, PASSWORD_HINT } from '@/shared/session/password';

/**
 * The invitation link (contract v10.10): `/invitacion/:token`, public. The page first asks
 * the API who the link is for, so the person sees their own address before typing
 * anything and a dead link says so instead of failing after the form. Then two password
 * fields, and a way to the door: accepting does not open a session on purpose, so the
 * person enters once with the password they just chose and the login is the same as
 * every other day.
 *
 * Same wall and logo as the door: it is the door, one step earlier.
 */
export function InvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const peek = useQuery({
    queryKey: ['invite', token],
    queryFn: () => authApi.peekInvite(token),
    retry: false,
  });
  const accept = useMutation<unknown, ApiError | Error, string>({
    mutationFn: (password) => authApi.acceptInvite(token, password),
  });

  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const mismatch = repeat.length > 0 && repeat !== password;
  const ready = password.length >= MIN_PASSWORD && repeat === password;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) return;
    accept.mutate(password);
  }

  return (
    <main className="bg-paper grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <section className="bg-nav relative hidden items-center justify-center overflow-hidden p-12 lg:flex">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1.2px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <Logo inverted className="relative h-28 w-auto" />
      </section>

      <section className="flex items-center justify-center px-5 py-10 sm:px-6 sm:py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex justify-center lg:hidden">
            <Logo className="h-14" />
          </div>

          {peek.isPending ? <Spinner variant="fill" label="Comprobando la invitación" /> : null}

          {peek.isError ? (
            <>
              <h2 className="font-display text-ink text-[26px] font-semibold">
                Este enlace ya no vale
              </h2>
              <p className="text-ink-3 mt-2 text-[15px]">
                No existe, ya se usó o han pasado las 24 horas. Pide otro a quien administra la
                aplicación.
              </p>
              <Link to="/entrar" className="link mt-6 inline-block text-[14px]">
                Ir a la puerta de entrada
              </Link>
            </>
          ) : null}

          {peek.data && accept.isSuccess ? (
            <>
              <h2 className="font-display text-ink text-[26px] font-semibold">
                Contraseña guardada
              </h2>
              <p className="text-ink-3 mt-2 text-[15px]">
                Ya puedes entrar con <strong>{peek.data.email}</strong> y la contraseña que acabas
                de elegir.
              </p>
              <Button
                type="button"
                size="lg"
                className="mt-6"
                onClick={() => void navigate('/entrar', { replace: true })}
              >
                Entrar
              </Button>
            </>
          ) : null}

          {peek.data && !accept.isSuccess ? (
            <>
              <h2 className="font-display text-ink text-[26px] font-semibold">
                Hola{peek.data.full_name ? `, ${peek.data.full_name}` : ''}
              </h2>
              <p className="text-ink-3 mt-1 text-[15px]">
                Elige la contraseña con la que entrarás como <strong>{peek.data.email}</strong>.
                Este enlace vale una sola vez.
              </p>

              <form onSubmit={submit} className="mt-7 flex flex-col gap-4" noValidate>
                <PasswordInput
                  label="Contraseña"
                  name="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_PASSWORD}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  hint={PASSWORD_HINT}
                />
                <PasswordInput
                  label="Repite la contraseña"
                  name="password-repeat"
                  autoComplete="new-password"
                  required
                  value={repeat}
                  onChange={(event) => setRepeat(event.target.value)}
                  error={mismatch ? 'No coincide con la de arriba.' : undefined}
                />

                {accept.isError ? (
                  <p
                    role="alert"
                    className="bg-danger-soft text-danger border-danger/20 flex items-start gap-2 rounded-md border px-3 py-2 text-[14px]"
                  >
                    <AlertIcon size={16} className="mt-0.5 shrink-0" />
                    <span>{errorMessage(accept.error)}</span>
                  </p>
                ) : null}

                <Button
                  type="submit"
                  size="lg"
                  disabled={!ready || accept.isPending}
                  className="mt-1"
                >
                  {accept.isPending ? 'Guardando…' : 'Guardar contraseña'}
                </Button>
              </form>
            </>
          ) : null}
        </div>
      </section>
    </main>
  );
}
